import { getOrganizationTreeFunctionDef, getOrgMemberCountsDef, } from '../../../../backend/features/organizations/shared';
import { requireAnySystemPermission } from '../../../../backend/features/permissions/shared';
import { addPendingMembershipsToOrgTree } from '../../../../ui/features/organizations/utils';
import { useUserState } from '../../../../ui/hooks';
import { useContactManagementTranslation } from '../../../../ui/hooks/useContactManagementTranslation';
import { useDataTreeUsingUrl, } from '@juvare/common-data-table-ui/DataTree';
import { useTypedPlatformFunctionResponse } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import React from 'react';
import { replaceOrganizationTreeBranch } from '../utils';
export function useOrganizationsTree({ useDataTreeFunction = useDataTreeUsingUrl, staticFilters, }) {
    var _a, _b;
    const { t } = useContactManagementTranslation();
    const userState = useUserState();
    const [parentId, setParentId] = React.useState();
    const expandedItems = React.useRef([]);
    const orgTree = React.useRef([]);
    const disablePartialLoading = React.useRef(false);
    const canAccessAll = requireAnySystemPermission(userState, 'view-invisible-organizations', 'all-organizations-admin');
    const regularUserFilters = {};
    if (!canAccessAll) {
        regularUserFilters.visible = true;
    }
    const { data: orgMembershipsData, isLoading: isMembershipsLoading, error: membershipError, mutate: membershipMutate, } = useTypedPlatformFunctionResponse(getOrgMemberCountsDef, {});
    const params = {
        useDataProvider: (request) => {
            const filters = Object.assign(Object.assign({}, staticFilters), request.filterData);
            const { search, permission, visible, orgPermissions, pending } = filters;
            const areAllFiltersUndefined = !Object.values(filters).some((value) => value !== undefined);
            const filterData = {
                search: !!search && search.length >= 3 ? search : undefined,
                permission: areAllFiltersUndefined ? undefined : permission,
                pending: pending ? pending : undefined,
            };
            const { data: organizationsTreeData, isLoading: organizationTreeLoading, error: organizationTreeError, mutate: organizationTreeMutate, } = useTypedPlatformFunctionResponse(getOrganizationTreeFunctionDef, {
                filterData: Object.keys(filterData).length > 0 ? filterData : undefined,
                staticFilters: {
                    visible: areAllFiltersUndefined ? regularUserFilters.visible : visible,
                    orgPermissions,
                    permission: areAllFiltersUndefined && !canAccessAll ? 'member' : undefined,
                },
                maxDepth: 3,
                parentId: parentId ? parentId.split('_')[0] : undefined,
                parentPath: parentId ? parentId : undefined,
                maxExpandedIds: 500,
            });
            const isLoading = organizationTreeLoading || isMembershipsLoading;
            let error = membershipError || organizationTreeError;
            if (!!search && search.length < 3) {
                error = new Error(t('panels.organizations.searchValidation'));
            }
            const data = React.useMemo(() => {
                let organizationTree = [];
                let highlightedIds = undefined;
                if (!isLoading && !error && organizationsTreeData && !!(orgMembershipsData === null || orgMembershipsData === void 0 ? void 0 : orgMembershipsData.organizationMembershipMap)) {
                    highlightedIds = organizationsTreeData.highlightedIds.length > 0 ? organizationsTreeData.highlightedIds : undefined;
                    disablePartialLoading.current = organizationsTreeData.highlightedIds.length > 0;
                    organizationTree = addPendingMembershipsToOrgTree(organizationsTreeData.organizations, orgMembershipsData.organizationMembershipMap);
                }
                if (parentId) {
                    if (search || permission) {
                        setParentId(undefined);
                    }
                    organizationTree = replaceOrganizationTreeBranch(orgTree.current, organizationTree, parentId);
                }
                orgTree.current = organizationTree;
                return {
                    items: organizationTree,
                    highlightedItemIds: highlightedIds,
                };
            }, [organizationsTreeData, isLoading, error, search, permission]);
            return {
                data,
                error: error,
                reload: () => {
                    setParentId(undefined);
                    membershipMutate();
                    organizationTreeMutate();
                },
                isLoading: isLoading,
            };
        },
    };
    const { dataTreeProps, filtersBarProps, appliedFilterData, reload } = useDataTreeFunction(params);
    const defaultDataTreeProps = Object.assign(Object.assign({}, dataTreeProps), { onChangeExpandedItemIds: (action) => {
            const data = typeof action === 'function' ? action(undefined) : action;
            if (data && data.length > 0 && !disablePartialLoading.current) {
                const newExpandedItem = data[0];
                if (!expandedItems.current.includes(newExpandedItem)) {
                    setParentId(newExpandedItem);
                    expandedItems.current.push(newExpandedItem);
                }
            }
            dataTreeProps.onChangeExpandedItemIds(action);
        }, customEmptyContentText: t('panels.organizations.noFilterResult'), title: t('panels.organizations.title'), 'data-test': 'organization-tree' });
    const selectedOrganizationIds = React.useMemo(() => {
        var _a;
        return (_a = dataTreeProps.selectedItemIds) === null || _a === void 0 ? void 0 : _a.map((id) => id.split('_')[0]);
    }, [dataTreeProps.selectedItemIds]);
    const activeOrganizationId = (_a = dataTreeProps.activeItemId) === null || _a === void 0 ? void 0 : _a.split('_')[0];
    return {
        dataTreeProps: defaultDataTreeProps,
        filtersBarProps,
        appliedFilterData,
        reload,
        totalPendingMemberships: (_b = orgMembershipsData === null || orgMembershipsData === void 0 ? void 0 : orgMembershipsData.totalMemberCount) !== null && _b !== void 0 ? _b : 0,
        activeOrganizationId,
        selectedOrganizationIds,
    };
}
/**
 * maps OrganizationsFilterData (UI filters) to FieldFilter (used in actual organization filtering)
 * */
export function mapOrganizationFilterData(filterData) {
    const fieldFilters = [];
    if (filterData.search) {
        fieldFilters.push({ field: 'search', value: filterData.search });
    }
    if (filterData.permission) {
        fieldFilters.push({ field: 'permission', value: filterData.permission });
    }
    if (filterData.pending) {
        fieldFilters.push({ field: 'pending', value: filterData.pending });
    }
    return fieldFilters;
}
